<template>
  <div class="toolbar gold_top_rule relative flex items-center justify-between gap-2 flex-wrap px-3 py-2 bg-bg-surface border border-border-strong rounded-lg shadow-md" role="toolbar" :aria-label="messages.toolbar.actionsAria">
    <div class="flex items-center gap-2">
      <span class="counts font-mono text-xs text-text-muted tracking-wide" aria-live="polite">
        {{ messages.toolbar.groups }} <strong class="font-semibold text-text-primary">{{ store.counts.groups }}</strong>
        · {{ messages.toolbar.players }} <strong class="font-semibold text-text-primary">{{ store.counts.players }}</strong>
      </span>
      <AppButton
        :disabled="disabled"
        :variant="activeFilterCount > 0 ? ButtonVariant.Primary : ButtonVariant.Secondary"
        :size="ButtonSize.Sm"
        :aria-pressed="activeFilterCount > 0 ? 'true' : 'false'"
        :aria-label="activeFilterCount > 0 ? messages.toolbar.openActiveFilters(filterMode, activeFilterCount) : messages.toolbar.openFilters"
        @click="emit('open-filters')"
      >
        <template #icon-left><AppIcon :name="IconName.Filter" :size="15" /></template>
        {{ messages.toolbar.filters }}
        <span v-if="activeFilterCount > 0" class="ml-1 rounded-full bg-bg/25 px-1.5 font-mono text-2xs">{{ activeFilterCount }}</span>
      </AppButton>
    </div>
    <span
      v-if="store.isMember && !store.isOfficer && store.viewMode === ViewMode.Compact"
      class="status status_view_only min-w-0 flex-1 text-center text-xs text-text-muted"
      role="status"
    >
      {{ messages.toolbar.viewOnly }}
    </span>
    <div class="toolbar_right inline-flex items-center gap-2 flex-wrap">
      <RaidPlanNavigation
        v-if="activeRaid !== null"
        :raids="raids"
        :active-raid="activeRaid"
        @select="emit('select-raid', $event)"
      />
      <div
        class="inline-flex items-center gap-1 rounded-md border border-border bg-bg-elevated p-0.5"
        role="radiogroup"
        :aria-label="messages.toolbar.playerNameModeAria"
      >
        <AppButton
          v-for="option in playerNameModeOptions"
          :key="option.mode"
          role="radio"
          :disabled="disabled"
          :variant="store.playerNameMode === option.mode ? ButtonVariant.Primary : ButtonVariant.Ghost"
          :size="ButtonSize.Sm"
          :aria-checked="store.playerNameMode === option.mode ? 'true' : 'false'"
          :aria-label="option.ariaLabel"
          @click="store.setPlayerNameMode(option.mode)"
        >{{ option.label }}</AppButton>
      </div>
      <AppButton
        v-if="store.activeRaidId !== null"
        :disabled="disabled"
        :variant="ButtonVariant.Secondary"
        :size="ButtonSize.Sm"
        :aria-label="messages.toolbar.openMap"
        @click="emit('open-map')"
      >
        {{ messages.toolbar.map }}
      </AppButton>
      <AppButton
        v-if="store.isOfficer && store.activeRaidId !== null"
        :disabled="disabled"
        :variant="ButtonVariant.Secondary"
        :size="ButtonSize.Sm"
        :aria-label="messages.toolbar.raidAuditAria"
        @click="emit('open-raid-audit')"
      >
        <template #icon-left><AppIcon :name="IconName.LayoutDetail" :size="15" /></template>
        {{ messages.toolbar.raidAudit }}
      </AppButton>
      <AppButton v-if="store.isOfficer" :disabled="disabled" :variant="ButtonVariant.Secondary" :size="ButtonSize.Sm" :aria-label="messages.toolbar.cloneRaidAria" @click="onCloneRaid">
        <template #icon-left><AppIcon :name="IconName.Copy" :size="15" /></template>
        {{ messages.toolbar.cloneRaid }}
      </AppButton>
      <AppButton
        v-if="store.isOfficer && !store.activeRaidEditLocked && raidPublished !== null"
        :disabled="disabled"
        :variant="raidPublished ? ButtonVariant.Secondary : ButtonVariant.Primary"
        :size="ButtonSize.Sm"
        :aria-label="raidPublished ? messages.toolbar.unpublishAria : messages.toolbar.publishAria"
        @click="emit('toggle-raid-publication')"
      >{{ raidPublished ? messages.toolbar.unpublish : messages.toolbar.publish }}</AppButton>
      <AppButton
        :disabled="disabled"
        :variant="ButtonVariant.Secondary"
        :size="ButtonSize.Sm"
        :aria-pressed="store.viewMode === ViewMode.Compact ? 'true' : 'false'"
        :aria-label="store.viewMode === ViewMode.Compact ? messages.toolbar.switchToDetail : messages.toolbar.switchToCompact"
        @click="toggleViewMode"
      >
        <template #icon-left><AppIcon :name="store.viewMode === ViewMode.Compact ? IconName.LayoutCompact : IconName.LayoutDetail" :size="15" /></template>
        {{ store.viewMode === ViewMode.Compact ? messages.toolbar.compact : messages.toolbar.detail }}
      </AppButton>
      <AppButton v-if="store.isOfficer && !store.activeRaidEditLocked" :disabled="disabled" :variant="ButtonVariant.Secondary" :size="ButtonSize.Sm" :aria-label="messages.toolbar.sortAria" @click="onSortAll">
        <template #icon-left><AppIcon :name="IconName.Sort" :size="15" /></template>
        {{ messages.toolbar.sort }}
      </AppButton>
      <AppButton v-if="store.isOfficer && !store.activeRaidEditLocked" :disabled="disabled" :variant="ButtonVariant.Danger" :size="ButtonSize.Sm" :aria-label="messages.toolbar.resetAria" @click="onReset">
        <template #icon-left><AppIcon :name="IconName.RotateCcw" :size="15" /></template>
        {{ messages.toolbar.reset }}
      </AppButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { defineLocaleCatalog } from '@/shared/i18n'
import { useLocaleMessages } from '@/shared/composables/useI18n'
import { AppButton, AppIcon, ButtonSize, ButtonVariant, IconName } from '@/shared/ui'
import { useViewTimeouts } from '@/shared/composables'

import RaidPlanNavigation from '../calendar/RaidPlanNavigation.vue'
import { boardEn } from './locales/en.ts'
import { boardRu } from './locales/ru.ts'
import { useGvgPlannerStore } from '../../stores/gvgPlannerStore'
import { PlayerFilterMode } from '../../lib/playerFilters'
import type { PlayerFilterMode as PlayerFilterModeType } from '../../lib/playerFilters'
import { PlayerNameMode, ViewMode } from '../../types'
import type { PlayerNameMode as PlayerNameModeType, RaidId, RaidSummary } from '../../types'

export interface PlannerToolbarProps {
  readonly disabled?: boolean
  readonly raidPublished?: boolean | null
  readonly activeFilterCount?: number
  readonly filterMode?: PlayerFilterModeType
  readonly raids?: ReadonlyArray<RaidSummary>
  readonly activeRaid?: RaidSummary | null
}

withDefaults(defineProps<PlannerToolbarProps>(), {
  disabled: false,
  raidPublished: null,
  activeFilterCount: 0,
  filterMode: PlayerFilterMode.Filter,
  raids: () => [],
  activeRaid: null,
})

const emit = defineEmits<{
  (event: 'open-map'): void
  (event: 'open-raid-audit'): void
  (event: 'clone-raid'): void
  (event: 'toggle-raid-publication'): void
  (event: 'open-filters'): void
  (event: 'sort-all'): void
  (event: 'reset'): void
  (event: 'select-raid', raidId: RaidId): void
}>()

const catalog = defineLocaleCatalog({ en: boardEn, ru: boardRu })
const messages = useLocaleMessages(catalog)
const store = useGvgPlannerStore()
const { announce, scheduleTimeout } = useViewTimeouts()

interface PlayerNameModeOption {
  readonly mode: PlayerNameModeType
  readonly label: string
  readonly ariaLabel: string
}

const playerNameModeOptions = computed<ReadonlyArray<PlayerNameModeOption>>(() => [
  {
    mode: PlayerNameMode.GuildNickname,
    label: messages.value.toolbar.guildNickname,
    ariaLabel: messages.value.toolbar.showGuildNickname,
  },
  {
    mode: PlayerNameMode.PlayerNickname,
    label: messages.value.toolbar.playerNickname,
    ariaLabel: messages.value.toolbar.showPlayerNickname,
  },
  {
    mode: PlayerNameMode.PlayerAccountName,
    label: messages.value.toolbar.playerAccountName,
    ariaLabel: messages.value.toolbar.showPlayerAccountName,
  },
])

function toggleViewMode(): void {
  store.setViewMode(store.viewMode === ViewMode.Compact ? ViewMode.Detail : ViewMode.Compact)
}

function onCloneRaid(): void {
  emit('clone-raid')
}

function onSortAll(): void {
  if (store.activeRaidEditLocked) return
  // F-86: the store action returns `void` and writes transport failures
  // into `store.error` via the mutation runner's catch block. Snapshot
  // `store.error` before dispatching and let one microtask pass so
  // Apollo's `.mutate()` Promise has a chance to settle on the
  // synchronous-rejection path; only claim success when no fresh
  // failure surfaced. The view-level `role="alert"` banner driven by
  // `store.error` handles the user-visible failure messaging — do not
  // announce success when the dispatch produced a fresh error.
  const errorBefore = store.error
  store.sortAllGroups()
  void Promise.resolve().then(() => {
    if (store.error !== null && store.error !== errorBefore) return
    announce(messages.value.toolbar.sortedAnnouncement)
  })
  emit('sort-all')
}

function onReset(): void {
  if (store.activeRaidEditLocked) return
  if (!window.confirm(messages.value.toolbar.resetConfirm)) return
  store.reset()
  announce(messages.value.toolbar.resetAnnouncement)
  scheduleTimeout(() => undefined, 1500)
  emit('reset')
}
</script>
